import { isRouteType } from "../../shared/stepConfig";
import {
  normalizeRouteAuth,
  type RouteAuthMode,
} from "../../shared/monitoringRouteAuth";

export type RouteAccessInput = {
  route: string;
  stepDir: string;
  title: string | null;
  routeType: string | null;
  routeAuth: string | null;
  routeGroups?: readonly string[] | null;
};

export type RouteAccessChange = {
  route: string;
  stepDir: string;
  title: string | null;
  routeType: string | null;
  from: RouteAuthMode | null;
  fromGroups: readonly string[];
  to: RouteAuthMode;
  toGroups: readonly string[];
};

export function groupsKey(groups: readonly string[]): string {
  return groups
    .map((name) => name.toLowerCase())
    .sort()
    .join("\n");
}

// No-sign-in scopes. A link that is new in the draft is only worth flagging
// when it lands on one of these, so routine new space-scoped links stay quiet.
const EXPOSED_SCOPES: RouteAuthMode[] = ["public", "external_id"];

export type RouteAccessPolicy = {
  publicWebpageAuthDisabled: boolean | null;
  externalIdWebpageAuthDisabled: boolean | null;
  publicNonWebpageAuthDisabled: boolean | null;
};

export function isRouteAccessDisabled(
  route: Pick<RouteAccessInput, "routeAuth" | "routeType">,
  forceDisabled: boolean,
  policy?: RouteAccessPolicy
): boolean {
  const auth = normalizeRouteAuth(route.routeAuth);
  if (auth !== "public" && auth !== "external_id") return false;
  const webpage = route.routeType === "webpage";
  if (auth === "external_id" && !webpage) return false;
  if (forceDisabled) return true;
  if (auth === "external_id") return !!policy?.externalIdWebpageAuthDisabled;
  if (!isRouteType(route.routeType)) {
    return !!(
      policy?.publicWebpageAuthDisabled || policy?.publicNonWebpageAuthDisabled
    );
  }
  return !!(webpage
    ? policy?.publicWebpageAuthDisabled
    : policy?.publicNonWebpageAuthDisabled);
}

export function changedRouteAccess(
  draftRoutes: readonly RouteAccessInput[],
  liveRoutes: readonly RouteAccessInput[],
  publicAccessDisabled = false,
  policy?: RouteAccessPolicy
): RouteAccessChange[] {
  const liveByRoute = new Map(liveRoutes.map((r) => [r.route, r]));

  const changes: RouteAccessChange[] = [];
  for (const r of draftRoutes) {
    const to = normalizeRouteAuth(r.routeAuth);
    const toGroups = r.routeGroups ?? [];
    const live = liveByRoute.get(r.route);
    const from = live ? normalizeRouteAuth(live.routeAuth) : null;
    const fromGroups = live?.routeGroups ?? [];
    const changed =
      from === null
        ? EXPOSED_SCOPES.includes(to)
        : from !== to || groupsKey(fromGroups) !== groupsKey(toGroups);
    if (changed || isRouteAccessDisabled(r, publicAccessDisabled, policy)) {
      changes.push({
        route: r.route,
        stepDir: r.stepDir,
        title: r.title,
        routeType: r.routeType,
        from,
        fromGroups,
        to,
        toGroups,
      });
    }
  }
  return changes;
}
